import React, { RefObject, useContext, useEffect, useRef, useState } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { Column } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'
import isEqual from 'lodash/isEqual'

import FilterContext from '@/components/pages/production/components/context'
import Map from '@/components/pages/production/components/map-stateful'
import MapboxMap from '@/components/pages/production/components/mapbox-map'
import StaticImageMap from '@/components/pages/production/components/static-image-map'
import ticketDetailsContext from '@/components/pages/production/components/ticket-details/context'
import { STYLES } from '@/constants'
import LayoutContext from '@/context/layout/context'
import ListingsContext from '@/context/listings'
import MapContext from '@/context/map'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { trackEvent } from '@/utils/analytics/ga360'

import { SectionsPriceAndColor } from '../context/types'
import MapTooltip from '../map-tooltip'

import styles from './styles.module.scss'

type PaddingType = 'tight' | 'default' | 'extra'

interface MapWrapperProps {
    show?: boolean
    withPanAndZoom?: boolean
    isDesktop?: boolean
    padding?: PaddingType
    showTooltip?: boolean
    isMiniMap?: boolean
    scrollRef?: RefObject<HTMLDivElement>
    displayFooter?: boolean
    toggleMap?: () => void
}

const MapWrapper: React.FC<WithOptimizelyProps & MapWrapperProps> = ({
    optimizely,
    show = true,
    withPanAndZoom = false,
    isDesktop,
    padding = 'default',
    showTooltip = true,
    isMiniMap = false,
    scrollRef,
    displayFooter = false,
    toggleMap,
}) => {
    const timer = useRef(0)
    const {
        state: { data: mapData, loading: isMapLoading },
    } = useContext(LayoutContext)
    const {
        state: { fetching: isListingResponseBeingFetched },
    } = useContext(ListingsContext)
    const [isMapReady, setIsMapReady] = useState(false)
    const [isMapboxMapVisible, setIsMapboxMapVisible] = useState<boolean>(false)
    const [showPadding, setShowPadding] = useState<PaddingType>(padding)
    const hasInteractiveMap = mapData?.jsonFileName || isMapboxMapVisible
    const minPrice = useRef(0)
    const pos = useRef({ x: 0, y: 0 })
    // Variable to enable checking that we have data to show
    const hasMapData = !!mapData
    const {
        state: filterState,
        selectors: {
            filteredSections: availables,
            filteredSectionsWithPriceAndColor,
            getHighlightedSections: selected,
        },
        dispatch: { onSectionSelectAction, onFilterResetAction, onSetSelectSimilarSnackbarVisibility },
    } = useContext(FilterContext)

    const {
        dispatch: { setHoveredSectionId, setIsMouseDown, setIsDoubleClicked },
        selectors: { mapState, isMapboxMapEnabled },
    } = useContext(MapContext)

    const {
        state: detailsState,
        dispatch: { toggleDetails },
    } = useContext(ticketDetailsContext)

    const availablesRef = useRef<string[]>([])
    const filteredSectionsWithPriceAndColorRef = useRef<SectionsPriceAndColor>({})

    useEffect(() => {
        availablesRef.current = availables
        filteredSectionsWithPriceAndColorRef.current = filteredSectionsWithPriceAndColor
    }, [availables, filteredSectionsWithPriceAndColor])

    const clickHandler = (event: any) => {
        if (isMiniMap) {
            optimizely?.track(OPTIMIZELY_EVENT.productionMinimapClick)
        }

        if (toggleMap && isMiniMap) {
            toggleMap()
            return
        }

        timer.current && clearTimeout(timer.current)

        timer.current = window.setTimeout(() => {
            const { id } = event.target

            if (!availables.includes(id) || (isDesktop && mapState.isDragging)) {
                if ((isEqual(id, 'toggle-map') || isEqual(event.target.parentNode.id, 'toggle-map')) && toggleMap) {
                    onFilterResetAction()
                    toggleMap()
                    return
                }
                return
            }
            if (scrollRef?.current?.scrollTop) scrollRef.current.scrollTop = 0
            if (detailsState.listing) {
                toggleDetails({ listing: null })
            }
            const isSectionDeselected = selected.includes(id)

            onSectionSelectAction({ id, hasSectionBeenManuallyFiltered: true })
            onSetSelectSimilarSnackbarVisibility({ showSnackbar: !isSectionDeselected })
            setHoveredSectionId('')
            trackEvent(
                'FilterUse',
                'ResetTicketFilters',
                `Zone-${isSectionDeselected ? 'Deselected' : 'Selected'}`,
                undefined,
                false,
            )
            /*
              This zoom event makes no sense but thats how they want it
              When a user clicks on a seat view in the map, we send section-tooltip
             */
            trackEvent('Seat View', 'zoom', 'section-tooltip', undefined, false)
        }, 200)
    }

    const doubleClickHandler = () => {
        timer.current && clearTimeout(timer.current)
        isDesktop && setIsDoubleClicked(true)
    }

    const mouseOverHandler = (event: any) => {
        const { id } = event.target

        if (event.target.nodeName === 'svg' || id === '') {
            pos.current = { x: 0, y: 0 }
            minPrice.current = 0
        }

        if (
            event.target.nodeName !== 'path' ||
            !availablesRef.current.includes(id) ||
            !filteredSectionsWithPriceAndColorRef.current[id]
        ) {
            setHoveredSectionId('')
            return
        }

        const { x, y, width, height } = event.target.getBoundingClientRect()

        pos.current = { x: x + width / 2, y: y + height / 2 }
        setHoveredSectionId(id)
    }

    const mouseLeaveHandler = () => {
        setHoveredSectionId('')
        pos.current = { x: 0, y: 0 }
        minPrice.current = 0
    }

    if (mapState.hoveredSectionId && filteredSectionsWithPriceAndColorRef.current[mapState.hoveredSectionId]) {
        minPrice.current = filterState.showAllInPrice
            ? filteredSectionsWithPriceAndColorRef.current[mapState.hoveredSectionId].allInPrice
            : filteredSectionsWithPriceAndColorRef.current[mapState.hoveredSectionId].price
    }

    useEffect(() => {
        setIsMapboxMapVisible(isMapboxMapEnabled)
        if (isMapboxMapEnabled) setShowPadding('tight')
    }, [isMapboxMapEnabled])
    useEffect(() => {
        if (!isMapLoading) {
            setIsMapReady(true)
        }
        return () => {
            setIsMapReady(false)
        }
    }, [isMapLoading])

    return show && hasMapData && isMapReady ? (
        <Column className={classNames(styles.mapWrapper, styles[`${showPadding}Padding`])}>
            {hasInteractiveMap ? (
                <>
                    {!isMapboxMapVisible && showTooltip && (
                        <MapTooltip x={pos.current.x} y={pos.current.y} price={minPrice.current} />
                    )}
                    {!isMapboxMapVisible && (
                        <Map
                            callbacks={{
                                onMouseOver: (event: any) => mouseOverHandler(event),
                                onMouseLeave: () => isDesktop && mouseLeaveHandler(),
                                onClick: clickHandler,
                                onDoubleClick: doubleClickHandler,
                                onPointerDownCapture: () => setIsMouseDown(true),
                                onPointerUpCapture: () => setIsMouseDown(false),
                            }}
                            withPanAndZoom={withPanAndZoom}
                            displayFooter={displayFooter}
                            availablesWithColor={filteredSectionsWithPriceAndColor}
                            sectionsStatus={{
                                selected: {
                                    style: STYLES.selectedSection,
                                    items: availables.some((available) => selected.includes(available)) ? selected : [],
                                },
                                hovered: {
                                    style: {
                                        ...STYLES.selectedSection,
                                        cursor: mapState.isMouseDown ? 'grabbing' : 'pointer',
                                    },
                                    items:
                                        !isMiniMap && availables.includes(mapState.hoveredSectionId)
                                            ? [mapState.hoveredSectionId]
                                            : [],
                                },
                            }}
                        />
                    )}
                    {!isListingResponseBeingFetched && isMapboxMapVisible && (
                        <MapboxMap displayFooter={displayFooter} />
                    )}
                </>
            ) : (
                <StaticImageMap isMiniMap={isMiniMap} callbacks={{ onClick: clickHandler }} />
            )}
        </Column>
    ) : null
}

export default withOptimizely(MapWrapper)
